import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft, LifeBuoy, Search, BookOpen, HelpCircle, Boxes } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { api } from "@/lib/api";
import { useHelp, moduleForPath } from "@/context/HelpContext";
import { Badge } from "@/components/ui-kit";

const CAT_ICON = { module: Boxes, system: BookOpen, faq: HelpCircle };
const CAT_LABEL = { module: "Module guide", system: "Setup guide", faq: "FAQ / troubleshooting" };

// Slide-out Help drawer. On open it detects the active module and shows its guide first,
// followed by relevant setup guides and FAQs. Includes an inline search.
export const HelpDrawer = () => {
  const { helpOpen, setHelpOpen } = useHelp();
  const location = useLocation();
  const moduleId = moduleForPath(location.pathname);

  const [topics, setTopics] = useState([]);
  const [q, setQ] = useState("");
  const [active, setActive] = useState(null); // full topic w/ content
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!helpOpen) return;
    setActive(null); setQ("");
    setLoading(true);
    api.get("/help/search", { params: { module_id: moduleId } })
      .then((r) => setTopics(r.data))
      .catch(() => setTopics([]))
      .finally(() => setLoading(false));
  }, [helpOpen, moduleId]);

  useEffect(() => {
    if (!helpOpen) return;
    const t = setTimeout(() => {
      api.get("/help/search", { params: { q, module_id: moduleId } })
        .then((r) => setTopics(r.data)).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [q]); // eslint-disable-line react-hooks/exhaustive-deps

  const openTopic = async (slug) => {
    setLoading(true);
    try { const { data } = await api.get(`/help/topics/${slug}`); setActive(data); }
    finally { setLoading(false); }
  };

  return (
    <Sheet open={helpOpen} onOpenChange={setHelpOpen}>
      <SheetContent side="right" className="w-full sm:max-w-md bg-zinc-950 border-zinc-800 text-zinc-100 flex flex-col p-0" data-testid="help-drawer">
        <SheetHeader className="p-5 border-b border-zinc-800">
          <SheetTitle className="flex items-center gap-2 text-zinc-50">
            <LifeBuoy className="h-5 w-5 text-indigo-400" /> {active ? active.title : "Help & Support"}
          </SheetTitle>
        </SheetHeader>

        {active ? (
          <div className="flex-1 overflow-y-auto">
            <button onClick={() => setActive(null)} data-testid="help-back"
              className="flex items-center gap-1.5 text-xs text-zinc-400 hover:text-zinc-200 px-5 pt-4">
              <ArrowLeft className="h-3.5 w-3.5" /> All topics
            </button>
            <article className="help-md px-5 py-4 text-sm text-zinc-300 leading-relaxed" data-testid="help-article">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{active.content}</ReactMarkdown>
            </article>
          </div>
        ) : (
          <>
            <div className="p-4 border-b border-zinc-800">
              <div className="relative">
                <Search className="h-4 w-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search help…" autoFocus
                  data-testid="help-search-input"
                  className="w-full bg-zinc-900 border border-zinc-800 rounded-lg pl-9 pr-3 py-2 text-sm text-zinc-100 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none transition-colors" />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-3 space-y-2" data-testid="help-topic-list">
              {loading ? <p className="text-xs text-zinc-500 p-3">Loading…</p>
                : topics.length === 0 ? <p className="text-xs text-zinc-500 p-3">No matching help topics.</p>
                : topics.map((t) => {
                  const Icon = CAT_ICON[t.category] || BookOpen;
                  return (
                    <button key={t.slug} onClick={() => openTopic(t.slug)} data-testid={`help-topic-${t.slug}`}
                      className="w-full text-left rounded-lg border border-zinc-800 bg-zinc-900 hover:border-zinc-700 p-3 transition-colors">
                      <div className="flex items-center gap-2">
                        <Icon className="h-4 w-4 text-indigo-400 flex-shrink-0" />
                        <span className="text-sm text-zinc-100 flex-1">{t.title}</span>
                        {t.module_id === moduleId && moduleId && <Badge tone="indigo">This module</Badge>}
                      </div>
                      {t.snippet && <p className="text-xs text-zinc-500 mt-1.5 line-clamp-2">{t.snippet}</p>}
                      <span className="text-[10px] uppercase tracking-wider text-zinc-600 mt-1.5 inline-block">{CAT_LABEL[t.category]}</span>
                    </button>
                  );
                })}
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
